/**
 * WebGL2 のシェーダ。
 *
 * 動画を 4 頂点クアッドへ載せる射影変換([ADR-0007](../../../docs/adr/0007-projection-rendering-webgl.md))。
 *
 * **考え方**: 頂点はクアッドの 4 隅(表示座標)をそのまま送り、
 * フラグメントごとに**逆変換**で**盤面の座標**を求めて UV を引く。
 * 頂点側で UV を線形補間すると射影の非線形性が失われる —— これが
 * 「アフィン変換では 4 点を自由に置けない」の実体。
 *
 * **写し元は盤面の矩形であって動画の矩形ではない**(#147 /
 * [ADR-0028](../../../docs/adr/0028-video-fit-and-panel-aspect.md))。
 * 動画は盤面の中へ `contain` で収め、余りは黒帯にする ——
 * 以前は動画の矩形をそのままクアッドへ写しており、縦横比が崩れていた。
 *
 * **盤面はセルに分割できる**(#152 /
 * [ADR-0029](../../../docs/adr/0029-panel-split-grid.md))。セルは
 * **同じ変換・同じ盤面座標系**のまま `uContentRect` だけを差し替えて描く ——
 * だから**シェーダはセルを知らない。**
 */
export const VERTEX_SHADER = `#version 300 es
in vec2 aPosition;          // ステージ座標(表示 px)
uniform vec2 uResolution;   // canvas のバッキングストア解像度
uniform float uDpr;
out vec2 vStage;

void main() {
  vStage = aPosition;
  vec2 clip = (aPosition * uDpr / uResolution) * 2.0 - 1.0;
  // Y を反転する(WebGL のクリップ空間は下が原点、DOM は上が原点)。
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
}`;
export const FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 vStage;
uniform mat3 uInverse;      // 表示座標 → **盤面の座標**(#147)
uniform vec2 uPanelSize;    // 盤面の寸法
uniform vec4 uContentRect;  // 盤面の中で素材が占める矩形(x, y, w, h)。**セルごとに変わる**(#152)
uniform sampler2D uTexture;
uniform float uHighlight;   // 0 = 通常、1 = 選択中(S04)
out vec4 outColor;

/*
 * 判定は 2 段(#147 / ADR-0028)。
 *
 *   盤面の外           -> discard
 *   盤面の内・素材の外 -> 黒帯
 *   素材の内           -> 標本化
 *
 * **以前は「動画の外 -> discard」の 1 段だった。** 動画の矩形全体を
 * クアッド全体へ写していたため、盤面と素材が常に一致していた
 * (= 引き伸ばし)。縦横比を保つと余りが出るので、帯を塗る段が増える。
 */
void main() {
  vec3 p = uInverse * vec3(vStage, 1.0);
  vec2 panel = p.xy / p.z;

  // クアッドの縁では数値誤差でわずかに外へ出る。伸びた 1px の帯が
  // 残るとつなぎ目が目立つため落とす。
  //
  // **判定は盤面に対して行う。セルに対して行ってはいけない**(#152 /
  // ADR-0029 の決定 3)。セルの境界で落とすと、**隣り合うセルの継ぎ目に
  // 1px の隙間が空く** —— セルの内側は塗り切り、外周だけを落とす。
  vec2 unit = panel / uPanelSize;
  if (unit.x < 0.0 || unit.x > 1.0 || unit.y < 0.0 || unit.y > 1.0) discard;

  // 盤面の内で素材の外は黒。**透明にしない** —— 背景写真が透けると
  // 盤面に穴が空いたように見える(ADR-0028)。
  vec2 uv = (panel - uContentRect.xy) / uContentRect.zw;
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    outColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }

  vec4 color = texture(uTexture, uv);
  // 選択中は明度を上げる。輪郭は SVG 側(S04)が描くので、ここは面の強調のみ。
  outColor = vec4(mix(color.rgb, min(color.rgb + 0.25, vec3(1.0)), uHighlight), color.a);
}`;
/** `highp` が使えない端末向けの精度指定。モバイルの一部で必要になる。 */
export const FRAGMENT_SHADER_MEDIUM_PRECISION = FRAGMENT_SHADER.replace('precision highp float;', 'precision mediump float;');
//# sourceMappingURL=shaders.js.map